import { inject, Injectable } from '@angular/core';
import { sliderConfig } from '@modules/gallery/gallery.configs';
import { SliderGalleryActionsService } from '@modules/gallery/services/actions/slider-gallery-actions.service';
import { SliderGalleryService } from '@modules/gallery/services/dialogs/sliders/slider-gallery.service';
import { Media } from '@pixacare/domain';
import { MediaHttpService } from '@services/http/media.http.service';
import { GalleryConfig } from '@shared/models/gallery-config';
import { GalleryItem } from '@shared/models/gallery-item';
import { MenuAction } from '@shared/models/menu-actions/menu-action';
import { MenuActionId } from '@shared/models/menu-actions/menu-action-id.enum';
import { forkJoin, Observable, of } from 'rxjs';
import { SadmGridStore } from './sadm-grid-store';

@Injectable()
export class SadmGridGalleryService extends SliderGalleryService<Media[], string> {
  private readonly mediaHttp = inject(MediaHttpService);
  private readonly sadmGridStore = inject(SadmGridStore);
  private readonly galleryActions = inject(SliderGalleryActionsService);

  /**
   * Entries are addressed by their stable client `entityKey` (the server id is absent for
   * unsaved drafts), so the gallery resolves its medias from the media store by key — no
   * server-id lookup. The base `openGallery(clientCode, entityKey, openIndex)` drives the rest.
   */
  protected getEntity(entityKey: string): Observable<Media[]> {
    const medias = this.sadmGridStore.get(entityKey)?.medias() ?? [];
    if (!medias.length) {
      return of([]);
    }
    return forkJoin(medias.map((media) => this.mediaHttp.getMedia(media.clientCode ?? '', media.id)));
  }

  protected getItems(medias: Media[]): GalleryItem[] {
    return medias.map((media) => ({ media }));
  }

  protected getConfig(): GalleryConfig {
    return { ...sliderConfig, quickActions: false };
  }

  protected getActions(): MenuAction[] {
    return [this.galleryActions.getAction(MenuActionId.GALLERY_SLIDER_DOWNLOAD)];
  }

  protected getWidget(): undefined {
    return undefined;
  }

  protected getImageLoadingErrorHandler(): (item: GalleryItem) => void {
    return (): void => {};
  }
}
